/**
 * Region Detection for LATAM vs US vs SPAIN — SOLO FALLBACK DE BROWSER.
 *
 * La VERDAD ÚNICA de location es el Worker (lib/canonicalLocation.ts):
 * este detectRegion() por timezone/idioma corre únicamente cuando el Worker
 * no resolvió país (dev local sin Worker). No agregar acá detección de
 * país/provincia para lógica de producto — usar getCanonicalLocation().
 * Los helpers de template por país/provincia que vivían en este archivo se
 * eliminaron: el template lo decide el Worker e inyecta en __TEMPLATE_CONFIG__.
 */

export type Region = "LATAM" | "US" | "SPAIN" | "OTHER";

// LATAM countries
const LATAM_COUNTRIES = [
  "AR", // Argentina
  "BO", // Bolivia
  "BR", // Brazil
  "CL", // Chile
  "CO", // Colombia
  "CR", // Costa Rica
  "CU", // Cuba
  "DO", // Dominican Republic
  "EC", // Ecuador
  "SV", // El Salvador
  "GT", // Guatemala
  "HN", // Honduras
  "MX", // Mexico
  "NI", // Nicaragua
  "PA", // Panama
  "PY", // Paraguay
  "PE", // Peru
  "PR", // Puerto Rico
  "UY", // Uruguay
  "VE", // Venezuela
];

/**
 * Detect user region based on browser locale and timezone
 */
export function detectRegion(): Region {
  if (typeof window === "undefined") return "LATAM"; // Default

  // Try to detect from timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // US timezones
  const US_TIMEZONES = [
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "America/Phoenix",
    "America/Anchorage",
    "America/Adak",
    "Pacific/Honolulu",
  ];

  if (US_TIMEZONES.includes(timezone)) {
    return "US";
  }

  // Spain timezones
  const SPAIN_TIMEZONES = [
    "Europe/Madrid",
    "Europe/Barcelona", // Same as Madrid but sometimes used
    "Atlantic/Canary",  // Canary Islands
  ];

  if (SPAIN_TIMEZONES.includes(timezone)) {
    return "SPAIN";
  }

  // LATAM timezones
  const LATAM_TIMEZONES = [
    "America/Argentina",
    "America/Sao_Paulo",
    "America/Mexico_City",
    "America/Bogota",
    "America/Lima",
    "America/Santiago",
    "America/Caracas",
    "America/Montevideo",
    "America/Asuncion",
    "America/La_Paz",
    "America/Guayaquil",
    "America/Havana",
    "America/Santo_Domingo",
    "America/Guatemala",
    "America/Tegucigalpa",
    "America/Managua",
    "America/San_Jose",
    "America/Panama",
  ];

  if (LATAM_TIMEZONES.some(tz => timezone.includes(tz.split("/")[1]))) {
    return "LATAM";
  }

  // Try to detect from navigator language
  const browserLang = navigator.language || (navigator as unknown as { userLanguage: string }).userLanguage;
  const countryCode = browserLang.split("-")[1]?.toUpperCase();

  if (countryCode === "US") {
    return "US";
  }

  if (countryCode === "ES") {
    return "SPAIN";
  }

  if (countryCode && LATAM_COUNTRIES.includes(countryCode)) {
    return "LATAM";
  }

  // Default to LATAM (since current site is in Spanish)
  return "LATAM";
}
